/**
 * #978 — card notes context + the page's single note panel.
 *
 * Mount this around a page's cards ONLY when a real adapter exists. With no
 * provider every `CardNoteControl` renders nothing, which is how the normal
 * Position Tracker route stays untouched until #976 delivers persistence:
 * no control, no read, no listener.
 *
 * The adapter is fixed for the provider's lifetime (key the provider to swap
 * it). `scope` is the resolved account scope from the integrator; changing it
 * — account/org switch, logout (null) — drops every record and any open draft
 * before the new scope can render.
 */
import { createContext, useContext, useLayoutEffect, useMemo, useState, type ReactNode } from 'react';
import { createCardNotesController, type CardNotesController } from '@/lib/cardNotes/cardNotesController';
import type { CardNoteAdapter } from '@/lib/cardNotes/types';
import { CardNotePanel } from './CardNotePanel';

/**
 * `scope` rides the context as well as the store: a control mounting in the
 * same commit as a switch reads the NEW scope from its props, before the
 * layout effect has reset the store, so it can never build an old-scope target.
 */
interface CardNotesContextValue {
  controller: CardNotesController;
  scope: string | null;
}

const CardNotesContext = createContext<CardNotesContextValue | null>(null);

export function useCardNotes(): CardNotesContextValue | null {
  return useContext(CardNotesContext);
}

export function CardNotesProvider({
  adapter,
  scope,
  children,
}: {
  adapter: CardNoteAdapter;
  /** null = no resolved identity: controls render disabled with a reason. */
  scope: string | null;
  children: ReactNode;
}) {
  const [controller] = useState(() => createCardNotesController(adapter, scope));
  // Layout effect: the reset lands before the browser paints the new scope.
  useLayoutEffect(() => controller.setScope(scope), [controller, scope]);
  const value = useMemo(() => ({ controller, scope }), [controller, scope]);
  return (
    <CardNotesContext.Provider value={value}>
      {children}
      <CardNotePanel controller={controller} />
    </CardNotesContext.Provider>
  );
}
